/**
 * Blunder Detection Algorithms
 *
 * This module implements two complementary approaches to detecting blunders:
 *
 * 1. Combinatorial Exhaustive Search - Tests every combination of k candidate
 *    rejections within a bounded budget of trial fits.
 *
 * 2. Iterative Data Snooping - Tests each residual component with Pope's tau
 *    statistic (Baarda's data snooping with an a-posteriori σ̂₀) and rejects
 *    the worst pair until every remaining component passes.
 *
 * Both methods share one candidate rule and one fit: candidates are the valid
 * Unity and Fixed pairs in the fit, Free and Ignore pairs never enter a fit or
 * a candidate set, and every trial is solved with the accepted weights,
 * solution mode rules, and scale setting, with the rejected pairs set to
 * Free. A trial is therefore exactly the fit a user would get by previewing
 * or applying that rejection. Rejecting a Fixed pair changes the number of
 * Fixed pairs, and with it the solution mode, just as it would on apply.
 */

import type {
  PointPair,
  BlunderResult,
  BlunderDetectionResults,
  DataSnoopingResult,
  DataSnoopingResults,
  DataSnoopingStopReason,
  PointResidual,
  TransformationSolution,
} from '@/types';
import { WEIGHT, isInFit } from '../weights';
import {
  DEFAULT_BETA,
  baardaNoncentrality,
  tauCriticalValue,
  twoSidedNormalCriticalValue,
  twoSidedStudentTCriticalValue,
} from '../statistics/distributions';
import {
  type CoreFit,
  type Observation,
  buildTransformationSolution,
  collectFitObservations,
  computeFitStatistics,
  fitObservations,
  isValidPointPair,
  residualAzimuth,
  residualOf,
  solveConformalTransformation,
} from './conformalTransformation';

/** Version stamped on results produced by these rules (see types). */
export const DETECTION_METHOD_VERSION = 2;

export interface DetectionOptions {
  /** Scale held for every trial fit; null or omitted estimates scale. */
  fixedScale?: number | null;
}

// ============================================================================
// Candidates
// ============================================================================

/**
 * Pairs either method may reject: valid pairs in the fit (Unity or Fixed).
 */
export const getBlunderCandidates = (
  points: readonly PointPair[]
): PointPair[] =>
  points.filter((p) => isInFit(p.weight) && isValidPointPair(p));

const labelLookup = (points: readonly PointPair[]) => {
  const labels = new Map(points.map((p) => [p.id, p.label]));
  return (id: number): string => labels.get(id) || String(id);
};

/** The points with one pair set to Free (rejected). */
const withRejected = (
  points: readonly PointPair[],
  rejectedId: number
): PointPair[] =>
  points.map((p) => (p.id === rejectedId ? { ...p, weight: WEIGHT.free } : p));

/** Largest residual among in-fit observations (first wins ties). */
const findMaxResidual = (
  observations: readonly Observation[],
  fit: CoreFit
): { total: number; id?: number; azimuth?: number } => {
  let max: { total: number; id?: number; azimuth?: number } = {
    total: -Infinity,
  };
  observations.forEach((o) => {
    const { ve, vn } = residualOf(o, fit);
    const total = Math.hypot(ve, vn);
    if (total > max.total) {
      max = { total, id: o.id, azimuth: residualAzimuth(ve, vn) };
    }
  });
  return max;
};

/** Largest non-null residual of a solution (first wins ties). */
const findSolutionMaxResidual = (
  residuals: readonly PointResidual[]
): PointResidual | undefined =>
  residuals.reduce<PointResidual | undefined>(
    (max, r) =>
      !r.excluded && r.total !== null && (!max || r.total > max.total!)
        ? r
        : max,
    undefined
  );

// ============================================================================
// Combination counting and generation
// ============================================================================

/** Survivors needed after rejection: three pairs leave r ≥ 2 in every mode. */
export const MIN_REMAINING_PAIRS = 3;

/** Highest rejection level offered. */
export const MAX_REJECTION_LEVEL = 8;

/**
 * Most trial fits a combinatorial request may evaluate (all levels of a
 * multi-level run combined). Measured on a desktop V8 worker, one trial
 * takes about 2.4 µs with 25 candidates, 5.4 µs with 50, and 9.2 µs with
 * 100, so the limit keeps a request near 0.6–2.3 s there and within seconds
 * on slower hardware. Unbounded, C(50, 8) ≈ 5.4e8 trials would take hours.
 */
export const MAX_COMBINATIONS = 250_000;

/** Rows returned by a "view all" run (best by RMS). */
export const MAX_LISTED_RESULTS = 1000;

/** Rows returned by a summary run (best by maximum residual). */
const SUMMARY_RESULT_COUNT = 10;

/** Binomial coefficient C(n, k); exact below 2^53, otherwise approximate. */
export const getCombinationCount = (n: number, k: number): number => {
  if (!Number.isInteger(n) || !Number.isInteger(k) || k < 0 || k > n) return 0;
  const kk = Math.min(k, n - k);
  let result = 1;
  for (let i = 1; i <= kk; i++) {
    result = (result * (n - kk + i)) / i;
  }
  return Math.round(result);
};

/**
 * Trial fits (excluding the no-rejection fit) for a rejection level, or for
 * every level from 1 to `level` when `allLevels` is set.
 */
export const getCombinatorialTrialCount = (
  candidateCount: number,
  level: number,
  allLevels = false
): number => {
  if (!allLevels) return getCombinationCount(candidateCount, level);
  let total = 0;
  for (let k = 1; k <= level; k++)
    total += getCombinationCount(candidateCount, k);
  return total;
};

/**
 * Highest level that respects the minimum survivors, the level cap, and
 * MAX_COMBINATIONS; 0 when none does. With `allLevels` (the default, since
 * one selector drives both run modes) the budget covers levels 1..k combined.
 */
export const getMaxFeasibleRejectionLevel = (
  candidateCount: number,
  allLevels = true
): number => {
  const structuralMax = Math.min(
    MAX_REJECTION_LEVEL,
    candidateCount - MIN_REMAINING_PAIRS
  );
  let best = 0;
  for (let k = 1; k <= structuralMax; k++) {
    if (
      getCombinatorialTrialCount(candidateCount, k, allLevels) >
      MAX_COMBINATIONS
    )
      break;
    best = k;
  }
  return best;
};

/** Feasibility and cost of a combinatorial request, for UI estimates. */
export interface CombinatorialPlan {
  candidateCount: number;
  level: number;
  allLevels: boolean;
  /** Trial fits excluding the no-rejection fit */
  combinations: number;
  maxFeasibleLevel: number;
  /** Null when the request can run; otherwise the reason it cannot */
  error: string | null;
}

/**
 * Validate a combinatorial request before any fit is computed. Use this to
 * disable infeasible levels and to show the number of combinations.
 */
export const planCombinatorialSearch = (
  points: readonly PointPair[],
  level: number,
  allLevels = false
): CombinatorialPlan => {
  const candidateCount = getBlunderCandidates(points).length;
  const maxFeasibleLevel = getMaxFeasibleRejectionLevel(
    candidateCount,
    allLevels
  );
  const valid = Number.isInteger(level) && level >= 1;
  const combinations = valid
    ? getCombinatorialTrialCount(candidateCount, level, allLevels)
    : 0;
  const plan = {
    candidateCount,
    level,
    allLevels,
    combinations,
    maxFeasibleLevel,
  };

  if (!valid || level > MAX_REJECTION_LEVEL) {
    return {
      ...plan,
      error: `Rejection level must be a whole number from 1 to ${MAX_REJECTION_LEVEL}; got ${level}.`,
    };
  }
  const minNeeded = level + MIN_REMAINING_PAIRS;
  if (candidateCount < minNeeded) {
    return {
      ...plan,
      error: `Minimum ${minNeeded} Unity or Fixed pairs required for ${level}-point rejection. You have ${candidateCount} candidate pairs.`,
    };
  }
  if (combinations > MAX_COMBINATIONS) {
    const advice =
      maxFeasibleLevel > 0
        ? `Choose level ${maxFeasibleLevel} or lower, or use iterative data snooping.`
        : 'Use iterative data snooping instead.';
    return {
      ...plan,
      error: `Rejection level ${level} would test ${combinations.toLocaleString('en-US')} combinations, above the limit of ${MAX_COMBINATIONS.toLocaleString('en-US')}. ${advice}`,
    };
  }
  return { ...plan, error: null };
};

/**
 * Lazily generate every k-combination of the indices 0..n−1 in
 * lexicographic order. Each yielded array is a fresh copy.
 */
export function* generateCombinations(
  n: number,
  k: number
): Generator<number[], void, undefined> {
  if (!Number.isInteger(n) || !Number.isInteger(k) || k < 0 || k > n) return;
  const indices = Array.from({ length: k }, (_, i) => i);
  for (;;) {
    yield indices.slice();
    let i = k - 1;
    while (i >= 0 && indices[i] === n - k + i) i--;
    if (i < 0) return;
    indices[i]++;
    for (let j = i + 1; j < k; j++) indices[j] = indices[j - 1] + 1;
  }
}

/**
 * Calculate the maximum rejection level offered for these points: the
 * highest level whose levels 1..k fit within MAX_COMBINATIONS, at least 1.
 */
export const calculateMaxRejectionLevel = (points: PointPair[]): number =>
  Math.max(
    1,
    getMaxFeasibleRejectionLevel(getBlunderCandidates(points).length, true)
  );

// ============================================================================
// Combinatorial Blunder Detection
// ============================================================================

interface TrialContext {
  observations: Observation[];
  weights: number[];
  fixedScale: number | null;
  labelOf: (id: number) => string;
}

const createTrialContext = (
  points: readonly PointPair[],
  fixedScale: number | null
): TrialContext => {
  const { observations, weights } = collectFitObservations(points);
  return { observations, weights, fixedScale, labelOf: labelLookup(points) };
};

/**
 * Fit with the candidates at `rejected` (indices) set to Free. Returns null
 * when the trial geometry cannot be solved.
 */
const evaluateTrial = (
  context: TrialContext,
  rejected: readonly number[]
): BlunderResult | null => {
  const skip = new Set(rejected);
  const observations: Observation[] = [];
  const weights: number[] = [];
  context.observations.forEach((o, i) => {
    if (skip.has(i)) return;
    observations.push(o);
    weights.push(context.weights[i]);
  });
  const result = fitObservations(observations, weights, context.fixedScale);
  if ('error' in result) return null;
  const { fit } = result;
  const statistics = computeFitStatistics(observations, weights, fit);
  const max = findMaxResidual(observations, fit);
  const rejectedIds = rejected.map((i) => context.observations[i].id);
  return {
    rejectedIds,
    rejectedLabels: rejectedIds.map(context.labelOf),
    rmsTotal: statistics.rmsTotal,
    maxResidual: max.total,
    maxResidualPointId: max.id,
    maxResidualAzimuth: max.azimuth,
    scale: fit.scale,
    rotation: fit.rotation,
    solutionMode: fit.mode,
    redundancy: statistics.redundancy,
    sigma0: statistics.sigma0,
  };
};

/** Keeps the best `limit` rows by `key`, stable for equal keys. */
const createRanking = (limit: number, key: (row: BlunderResult) => number) => {
  const rows: BlunderResult[] = [];
  return {
    rows,
    add(row: BlunderResult) {
      const value = key(row);
      if (rows.length >= limit && value >= key(rows[rows.length - 1])) return;
      let lo = 0;
      let hi = rows.length;
      while (lo < hi) {
        const mid = (lo + hi) >> 1;
        if (key(rows[mid]) <= value) lo = mid + 1;
        else hi = mid;
      }
      rows.splice(lo, 0, row);
      if (rows.length > limit) rows.pop();
    },
  };
};

/** Run one validated level and rank its rows (the initial row included). */
const runLevel = (
  context: TrialContext,
  level: number,
  viewAll: boolean,
  initial: BlunderResult | null,
  onProgress?: (percent: number, message: string) => void
): BlunderResult[] => {
  const ranking = viewAll
    ? createRanking(MAX_LISTED_RESULTS, (r) => r.rmsTotal)
    : createRanking(SUMMARY_RESULT_COUNT, (r) => r.maxResidual);
  if (initial) ranking.add(initial);

  const total = getCombinationCount(context.observations.length, level);
  const progressInterval = Math.max(100, Math.ceil(total / 100));
  let index = 0;
  for (const combo of generateCombinations(
    context.observations.length,
    level
  )) {
    if (onProgress && index % progressInterval === 0) {
      onProgress(
        ((index + 1) / (total + 1)) * 100,
        `Testing combination ${index + 1} of ${total}...`
      );
    }
    index++;
    const row = evaluateTrial(context, combo);
    if (row) ranking.add(row);
  }
  return ranking.rows;
};

const combinatorialMetadata = (
  candidateCount: number,
  fixedScale: number | null
) => ({
  methodVersion: DETECTION_METHOD_VERSION,
  candidateCount,
  ...(fixedScale !== null && { fixedScale }),
});

/**
 * Run blunder detection using combinatorial exhaustive search
 *
 * Fails fast, before any fit, when the level is invalid, leaves too few
 * pairs, or exceeds MAX_COMBINATIONS.
 *
 * @param points - Array of point pairs
 * @param rejectionLevel - Number of points to reject in each combination
 * @param viewAll - Return up to MAX_LISTED_RESULTS rows by RMS instead of
 *   the best 10 by maximum residual
 * @param onProgress - Optional callback for progress updates
 * @param options - Scale held for every trial fit
 * @returns BlunderDetectionResults
 */
export const runBlunderDetection = (
  points: PointPair[],
  rejectionLevel: number,
  viewAll = false,
  onProgress?: (percent: number, message: string) => void,
  options: DetectionOptions = {}
): BlunderDetectionResults => {
  const plan = planCombinatorialSearch(points, rejectionLevel);
  if (plan.error) return { error: plan.error };

  const fixedScale = options.fixedScale ?? null;
  const context = createTrialContext(points, fixedScale);
  const initial = evaluateTrial(context, []);
  const results = runLevel(
    context,
    rejectionLevel,
    viewAll,
    initial && { ...initial, isInitial: true },
    onProgress
  );

  return {
    results,
    rejectionLevel,
    totalCombinations: plan.combinations + 1,
    viewAll,
    ...combinatorialMetadata(plan.candidateCount, fixedScale),
  };
};

/**
 * Run blunder detection for all levels from 1 to maxLevel (clamped to
 * MAX_REJECTION_LEVEL and to the candidates that leave three pairs). Fails
 * fast when the combined levels exceed MAX_COMBINATIONS.
 */
export const runAllBlunderDetection = (
  points: PointPair[],
  maxLevel: number,
  onProgress?: (percent: number, message: string, level?: number) => void,
  options: DetectionOptions = {}
): BlunderDetectionResults => {
  const candidateCount = getBlunderCandidates(points).length;
  const effectiveMaxLevel = Math.min(
    maxLevel,
    MAX_REJECTION_LEVEL,
    candidateCount - MIN_REMAINING_PAIRS
  );

  if (effectiveMaxLevel < 1) {
    return {
      error: `Minimum ${MIN_REMAINING_PAIRS + 1} Unity or Fixed pairs required for blunder detection. You have ${candidateCount} candidate pairs.`,
    };
  }
  const plan = planCombinatorialSearch(points, effectiveMaxLevel, true);
  if (plan.error) return { error: plan.error };

  const fixedScale = options.fixedScale ?? null;
  const context = createTrialContext(points, fixedScale);
  const initialRow = evaluateTrial(context, []);
  const initial = initialRow && { ...initialRow, isInitial: true };

  const allResults: BlunderDetectionResults = {
    summary: [],
    allLevels: {},
    maxLevel: effectiveMaxLevel,
    isAllLevels: true,
    activePointCount: candidateCount,
    ...(initial ? { initial } : {}),
    ...combinatorialMetadata(candidateCount, fixedScale),
  };

  for (let level = 1; level <= effectiveMaxLevel; level++) {
    onProgress?.(
      ((level - 1) / effectiveMaxLevel) * 100,
      `Running level ${level}...`,
      level
    );
    const results = runLevel(context, level, false, initial, (percent, msg) =>
      onProgress?.(
        ((level - 1 + percent / 100) / effectiveMaxLevel) * 100,
        msg,
        level
      )
    );
    const totalCombinations = getCombinationCount(candidateCount, level) + 1;
    const best = results.find((r) => !r.isInitial) ?? results[0];
    if (best) {
      allResults.summary!.push({ level, ...best, totalCombinations });
    }
    allResults.allLevels![level] = {
      results,
      rejectionLevel: level,
      totalCombinations,
      viewAll: false,
      ...combinatorialMetadata(candidateCount, fixedScale),
    };
  }

  onProgress?.(100, 'Complete');
  return allResults;
};

// ============================================================================
// Residual cofactors
// ============================================================================

/** Residual cofactors of one pair's north and east components. */
export interface ResidualCofactor {
  id: number;
  weight: number;
  /** Mean of the two component cofactors (equal unless scale is held) */
  qvv: number;
  /** Mean of the two component redundancy numbers */
  redundancyNumber: number;
  qvvN: number;
  qvvE: number;
  redundancyNumberN: number;
  redundancyNumberE: number;
  /** False for a Fixed pair held exactly (no residual freedom) */
  testable: boolean;
}

const TESTABLE_REDUNDANCY = 1e-10;

/**
 * Residual cofactors q_vv = 1/w − h for each component, where h is the
 * diagonal of A·N⁻¹·Aᵀ for the fit's mode (centroid- or anchor-reduced):
 *
 * - standard, scale estimated: h = 1/Σw + d²/D for both components;
 * - standard, scale held: h_E = 1/Σw + u_N²/D, h_N = 1/Σw + u_E²/D, with u
 *   the reduced source point rotated into the target frame (only rotation
 *   is estimated, so only the tangential direction loses freedom);
 * - one Fixed pair: the same without the 1/Σw translation term, reduced to
 *   the Fixed pair; that pair itself has none;
 * - two Fixed pairs: h = 0 for check pairs; the Fixed pairs have none.
 *
 * The redundancy numbers w·q_vv of all components sum to the fit's r.
 */
const computeComponentCofactors = (
  observations: readonly Observation[],
  weights: readonly number[],
  fit: CoreFit
): ResidualCofactor[] => {
  const anchors = new Set(fit.anchorIndices);
  const D = fit.sumWeightedSquaredDistance;
  const translationTerm = fit.mode === 'standard' ? 1 / fit.sumW : 0;
  const { a, b } = fit.params;

  return observations.map((o, i) => {
    const w = weights[i];
    if (anchors.has(i)) {
      return {
        id: o.id,
        weight: w,
        qvv: 0,
        redundancyNumber: 0,
        qvvN: 0,
        qvvE: 0,
        redundancyNumberN: 0,
        redundancyNumberE: 0,
        testable: false,
      };
    }
    let hN = 0;
    let hE = 0;
    if (fit.mode !== 'constrained' && D > 0) {
      const x = o.e1 - fit.origin1.e;
      const y = o.n1 - fit.origin1.n;
      if (fit.scaleFixed && fit.scale > 0) {
        const uE = (a * x + b * y) / fit.scale;
        const uN = (-b * x + a * y) / fit.scale;
        hE = translationTerm + (uN * uN) / D;
        hN = translationTerm + (uE * uE) / D;
      } else {
        hE = hN = translationTerm + (x * x + y * y) / D;
      }
    }
    const qvvN = Math.max(0, 1 / w - hN);
    const qvvE = Math.max(0, 1 / w - hE);
    return {
      id: o.id,
      weight: w,
      qvv: (qvvN + qvvE) / 2,
      redundancyNumber: (w * (qvvN + qvvE)) / 2,
      qvvN,
      qvvE,
      redundancyNumberN: w * qvvN,
      redundancyNumberE: w * qvvE,
      testable: Math.max(w * qvvN, w * qvvE) > TESTABLE_REDUNDANCY,
    };
  });
};

/**
 * Compute residual cofactors of the pairs in the fit, for the solution mode
 * the weights imply. `qvv` is the per-component diagonal cofactor (the mean
 * of north and east, which are equal unless scale is held), and
 * `redundancyNumber` is w·qvv. Degenerate geometry gives zero cofactors.
 */
export const computeRedundancyNumbers = (
  points: PointPair[],
  fixedScale: number | null = null
): ResidualCofactor[] => {
  const { observations, weights } = collectFitObservations(points);
  const result = fitObservations(observations, weights, fixedScale);
  if ('error' in result) {
    return observations.map((o, i) => ({
      id: o.id,
      weight: weights[i],
      qvv: 0,
      redundancyNumber: 0,
      qvvN: 0,
      qvvE: 0,
      redundancyNumberN: 0,
      redundancyNumberE: 0,
      testable: false,
    }));
  }
  return computeComponentCofactors(observations, weights, result.fit);
};

// ============================================================================
// Tau test design
// ============================================================================

/** The tau test needs r − 1 ≥ 1 degrees of freedom. */
export const MIN_TAU_REDUNDANCY = 2;

/** Whether alpha is a usable two-sided significance level. */
export const isValidSignificanceLevel = (alpha: number): boolean =>
  Number.isFinite(alpha) && alpha > 0 && alpha < 0.5;

export interface TauTestDesign {
  alpha: number;
  redundancy: number;
  /** False when r < MIN_TAU_REDUNDANCY; no critical value exists */
  testable: boolean;
  /** Tau critical value; undefined when not testable */
  criticalValue?: number;
  /** Two-sided Student-t quantile with r − 1 degrees of freedom */
  studentT?: number;
  /** Known-σ₀ normal critical value z(1 − α/2) */
  normalCriticalValue: number;
  /** MDB non-centrality δ₀ = z(1 − α/2) + z(1 − β) */
  noncentrality: number;
  /** Power 1 − β assumed for MDB */
  power: number;
  /**
   * True when t(α, r − 1) ≥ δ₀: with σ₀ estimated from so few residuals, a
   * blunder of the reported MDB size is more likely to pass than be rejected.
   */
  lowPower: boolean;
}

/**
 * Critical value and power assessment of the tau test at a redundancy.
 * The tau test is equivalent to a t-test with r − 1 degrees of freedom on
 * the externally studentized residual, so t(α, r − 1) is the size a blunder
 * must typically reach, in standard deviations of its residual, to be
 * rejected; with known σ₀ that size would be z(α). Throws for an invalid
 * alpha (see isValidSignificanceLevel).
 */
export const describeTauTest = (
  alpha: number,
  redundancy: number,
  beta: number = DEFAULT_BETA
): TauTestDesign => {
  if (!isValidSignificanceLevel(alpha)) {
    throw new RangeError(
      `Significance level must be in (0, 0.5); got ${alpha}`
    );
  }
  const normalCriticalValue = twoSidedNormalCriticalValue(alpha);
  const noncentrality = baardaNoncentrality(alpha, beta);
  const base = {
    alpha,
    redundancy,
    normalCriticalValue,
    noncentrality,
    power: 1 - beta,
  };
  if (!(redundancy >= MIN_TAU_REDUNDANCY)) {
    return { ...base, testable: false, lowPower: true };
  }
  const studentT = twoSidedStudentTCriticalValue(alpha, redundancy - 1);
  return {
    ...base,
    testable: true,
    criticalValue: tauCriticalValue(alpha, redundancy),
    studentT,
    lowPower: studentT >= noncentrality,
  };
};

// ============================================================================
// Iterative Data Snooping
// ============================================================================

interface SnoopingStep {
  solution: TransformationSolution;
  design: TauTestDesign;
  /** Largest tau among testable components; null when nothing is testable */
  worst: {
    id: number;
    tau: number;
    redundancyNumber: number;
    mdb: number;
  } | null;
}

/**
 * Residuals at the rounding floor carry no information: a σ̂₀ this small
 * means an exact fit, and tau would only rank rounding noise.
 */
const isRoundingNoise = (
  sigma0: number,
  observations: readonly Observation[]
): boolean => {
  const magnitude = observations.reduce(
    (max, o) => Math.max(max, Math.abs(o.e2), Math.abs(o.n2), 1),
    1
  );
  return sigma0 <= 64 * Number.EPSILON * magnitude;
};

/**
 * Solve the current working points and compute each testable component's
 * tau, cofactor, redundancy number, and MDB (σ̂₀·δ₀ / (w·√q_vv), i.e. the
 * observation's σ = σ̂₀/√w times δ₀/√(w·q_vv)). The worst component per pair
 * is written onto that pair's residual.
 */
const snoopOnce = (
  workingPoints: readonly PointPair[],
  alpha: number,
  fixedScale: number | null
): SnoopingStep | { error: string } => {
  const { observations, weights } = collectFitObservations(workingPoints);
  const result = fitObservations(observations, weights, fixedScale);
  if ('error' in result) return { error: result.error };
  const solution = buildTransformationSolution(
    workingPoints,
    observations,
    weights,
    result.fit
  );
  const design = describeTauTest(alpha, solution.redundancy);
  const sigma0 = solution.sigma0;
  if (
    !design.testable ||
    sigma0 === undefined ||
    isRoundingNoise(sigma0, observations)
  ) {
    return { solution, design, worst: null };
  }

  const residualById = new Map(solution.residuals.map((r) => [r.id, r]));
  let worst: SnoopingStep['worst'] = null;
  for (const cofactor of computeComponentCofactors(
    observations,
    weights,
    result.fit
  )) {
    const residual = residualById.get(cofactor.id);
    if (!cofactor.testable || !residual) continue;
    if (residual.vn === null || residual.ve === null) continue;
    const w = cofactor.weight;
    let best: { tau: number; qvv: number } | null = null;
    for (const [v, qvv] of [
      [residual.vn, cofactor.qvvN],
      [residual.ve, cofactor.qvvE],
    ]) {
      if (w * qvv <= TESTABLE_REDUNDANCY) continue;
      const tau = Math.abs(v) / (sigma0 * Math.sqrt(qvv));
      if (best === null || tau > best.tau) best = { tau, qvv };
    }
    if (best === null) continue;
    const redundancyNumber = w * best.qvv;
    const mdb = (sigma0 * design.noncentrality) / (w * Math.sqrt(best.qvv));
    residual.standardizedResidual = best.tau;
    residual.residualCofactor = best.qvv;
    residual.redundancyNumber = redundancyNumber;
    residual.minimumDetectableBias = mdb;
    if (worst === null || best.tau > worst.tau) {
      worst = { id: cofactor.id, tau: best.tau, redundancyNumber, mdb };
    }
  }
  return { solution, design, worst };
};

/** Max-residual summary of a solution, labeled for display. */
const summarizeMaxResidual = (
  solution: TransformationSolution,
  labelOf: (id: number) => string
) => {
  const max = findSolutionMaxResidual(solution.residuals);
  return {
    maxResidual: max?.total ?? undefined,
    maxResidualPointId: max?.id,
    maxResidualAzimuth: max?.azimuth,
    maxResidualLabel: max ? labelOf(max.id) : undefined,
  };
};

const prefixInitial = (max: ReturnType<typeof summarizeMaxResidual>) => ({
  initialMaxResidual: max.maxResidual,
  initialMaxResidualPointId: max.maxResidualPointId,
  initialMaxResidualAzimuth: max.maxResidualAzimuth,
  initialMaxResidualLabel: max.maxResidualLabel,
});

/** Turn one snooping step into its iteration record. */
const toIteration = (
  iteration: number,
  step: SnoopingStep,
  labelOf: (id: number) => string
): DataSnoopingResult => {
  const { solution, design, worst } = step;
  const rejected =
    worst !== null &&
    design.criticalValue !== undefined &&
    worst.tau > design.criticalValue
      ? worst
      : null;
  return {
    iteration,
    rejectedPointId: rejected?.id ?? null,
    solution,
    passed: rejected === null,
    ...summarizeMaxResidual(solution, labelOf),
    criticalValue: design.criticalValue,
    redundancy: solution.redundancy,
    lowPower: design.lowPower,
    maxStandardizedResidual: worst?.tau,
    maxStandardizedResidualPointId: worst?.id,
    ...(rejected && {
      rejectedLabel: labelOf(rejected.id),
      standardizedResidual: rejected.tau,
      redundancyNumber: rejected.redundancyNumber,
      minimumDetectableBias: rejected.mdb,
    }),
  };
};

/**
 * Run Iterative Data Snooping to detect blunders
 *
 * Each iteration:
 * 1. Solves with the current weights, solution mode rules, and scale setting
 * 2. Computes Pope's tau for every testable residual component
 *    (v / (σ̂₀·√q_vv), with σ̂₀ the a-posteriori standard error of unit weight)
 * 3. Compares the largest |tau| with the tau critical value for the fit's
 *    redundancy r: t·√r / √(r − 1 + t²), t the two-sided Student-t quantile
 *    with r − 1 degrees of freedom
 * 4. Rejects that pair (sets it to Free) and repeats, or stops when it passes
 *
 * Testing needs r ≥ 2. If the first fit has less, the result is an error
 * flagged `insufficientRedundancy`; if rejections later reduce r below 2,
 * iterations stop with the same flag and no error.
 *
 * @param points - Array of point pairs
 * @param alpha - Two-sided significance level per component, 0 < alpha < 0.5
 *   (default 0.001)
 * @param onProgress - Optional callback for progress updates
 * @param options - Scale held for every fit
 * @returns DataSnoopingResults
 */
export const runIterativeDataSnooping = (
  points: PointPair[],
  alpha = 0.001,
  onProgress?: (percent: number, message: string) => void,
  options: DetectionOptions = {}
): DataSnoopingResults => {
  const fixedScale = options.fixedScale ?? null;
  const base: DataSnoopingResults = {
    iterations: [],
    finalRejectedIds: [],
    criticalValue: 0,
    alpha,
    methodVersion: DETECTION_METHOD_VERSION,
    testStatistic: 'tau',
    ...(fixedScale !== null && { fixedScale }),
  };
  if (!isValidSignificanceLevel(alpha)) {
    return {
      ...base,
      error: `Significance level alpha must be between 0 and 0.5 (exclusive); received ${alpha}.`,
    };
  }

  const labelOf = labelLookup(points);
  const iterations: DataSnoopingResult[] = [];
  const rejectedIds: number[] = [];
  let workingPoints = points.map((p) => ({ ...p }));
  let initialSolution: TransformationSolution | undefined;
  let first: TauTestDesign | undefined;
  let last: TauTestDesign | undefined;
  let stopReason: DataSnoopingStopReason = 'noTestableObservations';
  const maxIterations = getBlunderCandidates(points).length;

  const summary = () => ({
    ...base,
    iterations,
    finalRejectedIds: [...rejectedIds],
    criticalValue: first?.criticalValue ?? 0,
    redundancy: first?.redundancy,
    normalCriticalValue: first?.normalCriticalValue,
    noncentrality: first?.noncentrality,
    power: first?.power,
    initialSolution,
    ...(initialSolution && {
      ...prefixInitial(summarizeMaxResidual(initialSolution, labelOf)),
    }),
  });

  // Each iteration either stops or rejects one in-fit pair, and testing
  // stops once r < 2, so at most maxIterations fits are needed.
  for (
    let iteration = 1;
    iteration <= Math.max(maxIterations, 1);
    iteration++
  ) {
    onProgress?.(
      (iteration / Math.max(maxIterations, 1)) * 100,
      `Iteration ${iteration}...`
    );
    const step = snoopOnce(workingPoints, alpha, fixedScale);
    if ('error' in step) return { ...summary(), error: step.error };

    if (iteration === 1) {
      initialSolution = step.solution;
      first = step.design;
      if (!step.design.testable) {
        return {
          ...summary(),
          lowPower: true,
          insufficientRedundancy: true,
          stopReason: 'insufficientRedundancy',
          error: `Insufficient redundancy for data snooping: the fit has r = ${step.solution.redundancy}, and the tau test needs r ≥ ${MIN_TAU_REDUNDANCY} (at least 3 Unity or Fixed pairs in a standard fit).`,
        };
      }
    }
    if (!step.design.testable) {
      stopReason = 'insufficientRedundancy';
      break;
    }

    last = step.design;
    const record = toIteration(iteration, step, labelOf);
    iterations.push(record);
    if (record.rejectedPointId === null) {
      stopReason = step.worst ? 'passed' : 'noTestableObservations';
      break;
    }
    rejectedIds.push(record.rejectedPointId);
    workingPoints = withRejected(workingPoints, record.rejectedPointId);
  }

  const finalSolution = solveConformalTransformation(workingPoints, fixedScale);
  onProgress?.(100, 'Complete');

  return {
    ...summary(),
    finalSolution: finalSolution.error ? undefined : finalSolution,
    lowPower:
      stopReason === 'insufficientRedundancy' || last?.lowPower === true,
    insufficientRedundancy: stopReason === 'insufficientRedundancy',
    stopReason,
  };
};
